import React, { useEffect, useState } from 'react';
import { getMyLscSupportIssues } from '../../api';
import type { LscSupportIssue } from '../../types';
import { parseSupportIssueError, supportIssueCategoryLabel } from '../../supportIssueCopy';
import LoadingState from '../LoadingState';

const MyReportedIssuesPanel: React.FC = () => {
  const [issues, setIssues] = useState<LscSupportIssue[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    void getMyLscSupportIssues()
      .then((rows) => {
        if (!cancelled) setIssues(rows);
      })
      .catch((err) => {
        if (!cancelled) setError(parseSupportIssueError(err));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  if (loading) {
    return <LoadingState message="Loading your reported issues..." />;
  }

  return (
    <div className="card space-y-3 text-sm" data-testid="my-reported-issues-panel">
      <div>
        <h2 className="font-semibold text-white">My reported issues</h2>
        <p className="text-secondary-400 text-xs mt-1">
          Reports you submitted from Stream Center. These are for support triage and do not change
          league settings.
        </p>
      </div>

      {error ? (
        <p className="text-red-300" role="alert" data-testid="my-reported-issues-error">
          {error}
        </p>
      ) : null}

      {!error && issues.length === 0 ? (
        <p className="text-secondary-500" data-testid="my-reported-issues-empty">
          You have not submitted any issues yet.
        </p>
      ) : null}

      {!error && issues.length > 0 ? (
        <ul className="space-y-3" data-testid="my-reported-issues-list">
          {issues.map((issue) => (
            <li
              key={issue.issue_id}
              className="rounded-lg border border-secondary-700 bg-secondary-900/40 p-3 space-y-1"
              data-testid="my-reported-issue-item"
            >
              <div className="flex flex-wrap items-center justify-between gap-2">
                <p className="font-medium text-white">{issue.title}</p>
                <span className="text-xs uppercase tracking-wide text-secondary-500">
                  {issue.status}
                </span>
              </div>
              <p className="text-secondary-400 text-xs">
                {supportIssueCategoryLabel(issue.category)} · Ref {issue.issue_id}
              </p>
              {issue.public_notes ? (
                <p className="text-secondary-300 text-xs">{issue.public_notes}</p>
              ) : null}
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
};

export default MyReportedIssuesPanel;
